import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, CheckCircle2, Upload, ShieldCheck, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, CTAButton, SectionLabel } from "@/components/common/Bits";
import { Reveal } from "@/components/motion/Reveal";
import { WhatsAppIcon } from "@/components/ui/FloatingWhatsApp";
import { Checkbox } from "@/components/ui/checkbox";
import { COMPANY, waLink, WA_MESSAGES } from "@/data/company";
import { SERVICES, SPECIAL_SOLUTIONS } from "@/data/services";
import { submitContactForm } from "@/services/api";
import { uploadEnquiryPhoto, validateImage, MAX_ENQUIRY_BYTES } from "@/services/storage";

const CUSTOMER_TYPES = ["Property Agent", "Homeowner", "Landlord", "Tenant", "Business", "Property Manager", "Other"];
const PROPERTY_TYPES = ["HDB", "Condominium", "Landed Property", "Office", "Retail", "Industrial", "Other"];
const CONTACT_METHODS = ["WhatsApp", "Phone Call", "Email"];
const ALL_SERVICES = [...SERVICES, ...SPECIAL_SOLUTIONS].map((s) => s.name);

const empty = {
    name: "",
    mobile: "",
    email: "",
    customerType: "",
    service: "",
    propertyType: "",
    location: "",
    date: "",
    contactMethod: "WhatsApp",
    details: "",
    consent: false,
    company: "", // honeypot — bots fill this in, people never see it
};

export default function Contact() {
    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [fileName, setFileName] = useState("");
    const [file, setFile] = useState(null);
    const [uploadPercent, setUploadPercent] = useState(0);

    const set = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please enter your full name.";
        const mobile = form.mobile.replace(/\s|\+65/g, "");
        if (!form.mobile.trim()) e.mobile = "Please enter your mobile number.";
        else if (!/^[689]\d{7}$/.test(mobile)) e.mobile = "Enter a valid 8-digit Singapore mobile number.";
        if (!form.email.trim()) e.email = "Please enter your email.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
        if (!form.customerType) e.customerType = "Please select a customer type.";
        if (!form.service) e.service = "Please select a service.";
        if (!form.propertyType) e.propertyType = "Please select a property type.";
        if (!form.details.trim()) e.details = "Please tell us a little about your project.";
        if (!form.consent) e.consent = "Please accept the privacy consent to continue.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onFile = (ev) => {
        const chosen = ev.target.files?.[0];
        if (!chosen) return;
        const problem = validateImage(chosen, MAX_ENQUIRY_BYTES);
        if (problem) {
            toast.error(problem);
            ev.target.value = "";
            return;
        }
        setFileName(chosen.name);
        setFile(chosen);
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) {
            toast.error("Please check the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            let attachmentPath = "";
            if (file) {
                const uploaded = await uploadEnquiryPhoto(file, setUploadPercent);
                attachmentPath = uploaded.path;
            }
            await submitContactForm({ ...form, attachmentPath });
            setSubmitted(true);
            toast.success("Enquiry received!");
        } catch (err) {
            toast.error(err?.message || "Something went wrong. Please try WhatsApp instead.");
        } finally {
            setSubmitting(false);
            setUploadPercent(0);
        }
    };

    const inputCls = (key) =>
        `w-full rounded-xl border bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-blue ${errors[key] ? "border-brand-red" : "border-brand-blue/15"}`;

    return (
        <>
            <SEO
                title="Contact — Get a Free Quote"
                path="/contact"
                description="Contact ProCare.SG for a free quote. WhatsApp us or send an enquiry for moving, cleaning, painting, repairs and more across Singapore."
            />
            <PageHero
                label="Contact Us"
                title="One Contact. Complete Property CARE."
                subtitle="Tell us what your property needs and our team will help coordinate the right services."
                image="/images/office-cleaning.webp"
            />

            <section className="py-16">
                <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[0.9fr_1.1fr]">
                    {/* Info */}
                    <Reveal>
                        <SectionLabel>Get in touch</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold text-brand-blue">Let’s plan your move</h2>
                        <p className="mt-4 text-brand-ink/70">The fastest way to reach us is WhatsApp — we usually reply quickly during working hours.</p>

                        <div className="mt-8">
                            <CTAButton href={waLink(WA_MESSAGES.general)} whatsapp testid="contact-whatsapp-cta">Chat With Us on WhatsApp</CTAButton>
                        </div>

                        <ul className="mt-10 space-y-5">
                            <li className="flex items-start gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-blue/5 text-brand-blue"><Phone className="h-5 w-5" /></div>
                                <div><p className="text-xs uppercase tracking-widest text-brand-red">Mobile</p><a href={`tel:${COMPANY.mobileRaw}`} className="text-brand-ink hover:text-brand-blue">{COMPANY.mobile}</a></div>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-blue/5 text-brand-blue"><Mail className="h-5 w-5" /></div>
                                <div><p className="text-xs uppercase tracking-widest text-brand-red">Email</p><a href={`mailto:${COMPANY.email}`} className="text-brand-ink hover:text-brand-blue">{COMPANY.email}</a></div>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-blue/5 text-brand-blue"><MapPin className="h-5 w-5" /></div>
                                <div><p className="text-xs uppercase tracking-widest text-brand-red">Address</p><p className="text-brand-ink">{COMPANY.address.line1}, {COMPANY.address.line2}<br />{COMPANY.address.line3}</p></div>
                            </li>
                        </ul>
                    </Reveal>

                    {/* Form */}
                    <Reveal delay={0.1}>
                        <div className="rounded-[28px] border border-brand-blue/10 bg-white p-7 shadow-brand sm:p-9">
                            {submitted ? (
                                <div className="flex flex-col items-center py-12 text-center" data-testid="contact-success">
                                    <CheckCircle2 className="h-16 w-16 text-brand-red" />
                                    <h3 className="mt-5 font-display text-2xl font-semibold text-brand-blue">Thank you.</h3>
                                    <p className="mt-3 max-w-sm text-brand-ink/60">Your enquiry has been received. Our team will contact you soon.</p>
                                    <button onClick={() => { setForm(empty); setFileName(""); setFile(null); setSubmitted(false); }} className="mt-6 text-sm font-bold text-brand-red hover:underline" data-testid="contact-reset">Submit another enquiry</button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <Field label="Full Name" error={errors.name}>
                                            <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls("name")} data-testid="field-name" placeholder="Your name" />
                                        </Field>
                                        <Field label="Mobile Number" error={errors.mobile}>
                                            <input type="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={inputCls("mobile")} data-testid="field-mobile" placeholder="e.g. 8421 2329" />
                                        </Field>
                                        <Field label="Email Address" error={errors.email} className="sm:col-span-2">
                                            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls("email")} data-testid="field-email" placeholder="you@example.com" />
                                        </Field>
                                        <Field label="Customer Type" error={errors.customerType}>
                                            <select value={form.customerType} onChange={(e) => set("customerType", e.target.value)} className={inputCls("customerType")} data-testid="field-customer-type">
                                                <option value="">Select…</option>
                                                {CUSTOMER_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Service Required" error={errors.service}>
                                            <select value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls("service")} data-testid="field-service">
                                                <option value="">Select…</option>
                                                {ALL_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Property Type" error={errors.propertyType}>
                                            <select value={form.propertyType} onChange={(e) => set("propertyType", e.target.value)} className={inputCls("propertyType")} data-testid="field-property-type">
                                                <option value="">Select…</option>
                                                {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Property Location">
                                            <input type="text" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls("location")} data-testid="field-location" placeholder="e.g. Punggol" />
                                        </Field>
                                        <Field label="Preferred Appointment Date">
                                            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls("date")} data-testid="field-date" />
                                        </Field>
                                        <Field label="Preferred Contact Method">
                                            <select value={form.contactMethod} onChange={(e) => set("contactMethod", e.target.value)} className={inputCls("contactMethod")} data-testid="field-contact-method">
                                                {CONTACT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Project Details" error={errors.details} className="sm:col-span-2">
                                            <textarea rows={4} value={form.details} onChange={(e) => set("details", e.target.value)} className={inputCls("details")} data-testid="field-details" placeholder="Tell us about your property and what you need…" />
                                        </Field>

                                        {/* File upload placeholder */}
                                        <div className="sm:col-span-2">
                                            <label className="mb-1.5 block text-sm font-semibold text-brand-blue">Upload a Photo <span className="font-normal text-brand-ink/60">(optional, images only, max 5MB)</span></label>
                                            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-brand-blue/25 bg-brand-surface px-4 py-3 text-sm text-brand-ink/60 hover:border-brand-blue/50" data-testid="field-file-label">
                                                <Upload className="h-5 w-5 text-brand-blue" />
                                                {fileName || "Choose an image of your property…"}
                                                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="field-file" />
                                            </label>
                                        </div>

                                        {/* Consent */}
                                        <div className="sm:col-span-2">
                                            <label className="flex cursor-pointer items-start gap-3">
                                                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="field-consent" className="mt-0.5" />
                                                <span className="text-sm text-brand-ink/70">I consent to ProCare.SG collecting and using my details to respond to this enquiry, in line with the <a href="/privacy-policy" className="font-semibold text-brand-blue underline">Privacy Policy</a>.</span>
                                            </label>
                                            {errors.consent && <p className="mt-1.5 text-xs text-brand-red">{errors.consent}</p>}
                                        </div>

                                        {/* Honeypot: positioned off-screen and hidden from assistive tech.
                                            Bots fill it in; the Cloud Function silently drops those submissions. */}
                                        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                                            <label htmlFor="company-website">Company website</label>
                                            <input
                                                id="company-website"
                                                type="text"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                value={form.company}
                                                onChange={(e) => set("company", e.target.value)}
                                            />
                                        </div>

                                        {/* Spam protection */}
                                        <div className="sm:col-span-2 flex items-center gap-2 text-xs text-brand-ink/60">
                                            <ShieldCheck className="h-4 w-4" /> Protected by spam filtering and submission rate limits.
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="contact-submit"
                                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-red px-7 py-3.5 text-sm font-bold text-white transition-all hover:shadow-brand-lg disabled:opacity-60 sm:w-auto"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                {uploadPercent > 0 && uploadPercent < 100 ? `Uploading photo… ${uploadPercent}%` : "Sending…"}
                                            </>
                                        ) : (
                                            <>Send Enquiry <WhatsAppIcon className="h-4 w-4" /></>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}

function Field({ label, error, className = "", children }) {
    return (
        <div className={className}>
            <label className="mb-1.5 block text-sm font-semibold text-brand-blue">{label}</label>
            {children}
            {error && <p className="mt-1.5 text-xs text-brand-red">{error}</p>}
        </div>
    );
}
